import Modal from 'antd/lib/modal/Modal';
import React, { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { ACTIVE_THEME_NAME } from '../branding';
import { PageContainer } from '../components/common';
import { PaperIcon } from '@nackle/paper';
import classNames from 'classnames';
import './styles.less';
import { useTranslations } from '../hooks/translations';
import { CaretLeftFilled, CaretRightFilled } from '@ant-design/icons';
import moment from 'moment';
import { NotificationContext } from './NotificationsContext';
import { getString } from '@nackle/intl-tools';


export const NotificationCarousel = forwardRef( ( { autoDisplay = false, showFeatured = false, onNotiRead = () => {}, loggingOut }, ref ) => {

    const { locale } = useTranslations();
    
    const [ currentIndex, setCurrentIndex ] = useState( 0 );

    const [ visible, setVisible ] = useState( loggingOut ? false : autoDisplay );

    const { notifications, featuredNotifications, loading, readNotification } = useContext( NotificationContext );

    const notiData = useMemo( () => showFeatured ? featuredNotifications : notifications, [ showFeatured, featuredNotifications, notifications ] );

    useImperativeHandle( ref, () => ( {
        open: ( id = null ) => {
            setVisible( true );
            if ( id != null ) {
                const startIndex = notiData?.findIndex( n => n.id == id );
                if ( startIndex > -1 ) {
                    setCurrentIndex( startIndex );
                }
            }
        }
    } ) );

    useEffect( () => {
        moment.locale( locale.substring( 0, 2 ) );
    }, [ locale ] );

    const onForward = () => {
        onRead( currentIndex );

        const newIndex = currentIndex + 1;
        if ( newIndex >= notiData.length ) {return;}

        setCurrentIndex( newIndex );
    };

    const onBackward = () => {
        onRead( currentIndex );

        const newIndex = currentIndex - 1;
        if ( newIndex < 0 ) {return;}

        setCurrentIndex( newIndex );
    };

    const onClose = () => {
        onRead( currentIndex );
        setVisible( false );
    };

    const onRead = ( index = currentIndex ) => {
        readNotification( notiData[ index ]?.id );
        onNotiRead?.();
    };

    // this allows the carousel to max out at a certain number of dots an not overflow
    function isDotDisabled( dotIndex, index ) {
        const maxDots = 11; // arbitrary, must be odd
        const dotRad = Math.floor( maxDots / 2 );

        if ( notiData.length <= maxDots ) {
            return false;
        }

        let upperBound = index + dotRad;
        let lowerBound = index - dotRad;

        if ( lowerBound < 0 ) {
            upperBound -= lowerBound;
        } else if ( upperBound >= notiData.length ) {
            lowerBound -= ( upperBound - notiData.length + 1 ); 
        }

        return dotIndex < lowerBound || dotIndex > upperBound;
    }

    const footerNav = useMemo( () => {

        return [
            <CaretLeftFilled key={ 'back' } disabled={ currentIndex == 0 } onClick={ onBackward } />,
                ...( notiData
                    .map( ( _, i ) => i == currentIndex ? 
                        <PaperIcon icon='Circle2' collection='ultimate' key={ 'index' } className={ 'highlight' } /> : 
                        <PaperIcon icon='Circle' collection='ultimate' key={ i } /> )
                    .filter( ( _, i ) => !isDotDisabled( i, currentIndex ) ) ),
            <CaretRightFilled key={ 'forward' } disabled={ currentIndex == notiData.length - 1 } onClick={ onForward } /> ];
    }, [ notiData, currentIndex ] );


    return loading || notiData == null || notiData?.length == 0 ? <></> :
    ( 
        <PageContainer>
            <Modal
                className={ classNames( 'notifications', 'notifications-carousel', ACTIVE_THEME_NAME, `index-${ currentIndex }` ) }
                title={ <>
                    <h3>{ getString( 'notification.modal.title' ) }</h3> 
                    <PaperIcon collection='antd-outlined' icon='Close' onClick={ _ => onClose() } />
                </> }
                visible={ visible }
                destroyOnClose={ true }
                onCancel={ () => {} }
                closable={ false }
                footer={ notiData.length <= 1 ? [] : footerNav }
                >

                <h3 dangerouslySetInnerHTML={ { __html: notiData[ currentIndex ].name } } />

                <span dangerouslySetInnerHTML={ { __html: notiData[ currentIndex ].description } } />

                <span className='subtitle'>{ moment.utc( notiData[ currentIndex ].publishedAt ).local().startOf( 'seconds' ).fromNow() }</span>

                <ul className='debug'>
                    <li>{ `${ currentIndex + 1 }/${ notiData.length }` }</li>
                    <li>{ JSON.stringify( notiData[ currentIndex ], null, 2 ) }</li>
                </ul>
                
            </Modal>
        </PageContainer>
    );
} );